import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StaffBookings from "@/pages/StaffBookings";
import { PageHeader, StatusBadge, fmtDate } from "@/components/shared";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDays, Clock, MapPin, User, GraduationCap, Eye, XCircle, Users, Dog } from "lucide-react";
import { toast } from "sonner";

export default function Bookings() {
  const { user } = useAuth();
  if (user?.role !== "client") return <StaffBookings />;
  return <ClientBookings />;
}

function ClientBookings() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [avail, setAvail] = useState({ dogs: [], classes: [], booked: [] });
  const [dogByClass, setDogByClass] = useState({});

  const load = async () => {
    const [b, a] = await Promise.all([api.get("/bookings"), api.get("/client/available-classes")]);
    setBookings(b.data); setAvail(a.data);
  };
  useEffect(() => { load(); }, []);

  const cancel = async (b) => { if (!window.confirm("Cancel this booking?")) return; try { await api.post(`/bookings/${b.id}/cancel`, { reason: "Cancelled by owner" }); toast.success("Booking cancelled"); await load(); } catch { toast.error("Failed"); } };
  const request = async (classId) => {
    const dogId = dogByClass[classId] || (avail.dogs[0] && avail.dogs[0].id);
    if (!dogId) return toast.error("Select a dog");
    try { await api.post(`/classes/${classId}/request-booking`, { dog_id: dogId }); toast.success("Booking requested — awaiting confirmation"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const isBooked = (classId, dogId) => avail.booked.some(([c, d]) => c === classId && d === dogId);

  return (
    <div>
      <PageHeader title="My Bookings" subtitle="Your class bookings and available classes for your dog." />
      <Tabs defaultValue="mine">
        <TabsList className="h-auto">
          <TabsTrigger value="mine" data-testid="bookings-tab-mine">My Bookings <span className="ml-1.5 text-xs text-muted-foreground">({bookings.length})</span></TabsTrigger>
          <TabsTrigger value="available" data-testid="bookings-tab-available">Book a Class <span className="ml-1.5 text-xs text-muted-foreground">({avail.classes.length})</span></TabsTrigger>
        </TabsList>

        <TabsContent value="mine" className="mt-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {bookings.map((b) => (
              <Card key={b.id} className="border-border" data-testid="my-booking-card">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-heading font-semibold">{b.class_name || "Class"}</div>
                    <StatusBadge value={b.booking_status} />
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-y-1.5 gap-x-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1.5"><Dog size={12} /> {b.dog_name}</span>
                    <span className="flex items-center gap-1.5"><CalendarDays size={12} /> {fmtDate(b.date)}</span>
                    <span className="flex items-center gap-1.5"><Clock size={12} /> {b.time || "—"}</span>
                    <span className="flex items-center gap-1.5"><User size={12} /> {b.trainer_name || "—"}</span>
                    <span className="flex items-center gap-1.5"><MapPin size={12} /> {b.location_name || "—"}</span>
                  </div>
                  <div className="mt-3 pt-3 border-t border-border/60 flex gap-2">
                    {b.class_id && <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => navigate(`/classes/${b.class_id}`)} data-testid="my-booking-view"><Eye size={14} /> View</Button>}
                    {!["cancelled", "attended", "no_show"].includes(b.booking_status) && <Button size="sm" variant="outline" className="gap-1.5 h-8 text-destructive" onClick={() => cancel(b)} data-testid="my-booking-cancel"><XCircle size={14} /> Cancel</Button>}
                  </div>
                </CardContent>
              </Card>
            ))}
            {bookings.length === 0 && <p className="text-muted-foreground col-span-full text-center py-12">No bookings yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="available" className="mt-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {avail.classes.map((c) => {
              const dogId = dogByClass[c.id] || (avail.dogs[0] && avail.dogs[0].id);
              const booked = isBooked(c.id, dogId);
              const full = c.effective_status === "full";
              return (
                <Card key={c.id} className="border-border" data-testid="available-class-card">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-heading font-semibold">{c.name}</div>
                      <StatusBadge value={c.effective_status} />
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-y-1.5 gap-x-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1.5"><CalendarDays size={12} /> {fmtDate(c.date)}</span>
                      <span className="flex items-center gap-1.5"><Clock size={12} /> {c.time || "—"}</span>
                      <span className="flex items-center gap-1.5"><User size={12} /> {c.trainer_name || "—"}</span>
                      <span className="flex items-center gap-1.5"><Users size={12} /> {c.available_spots} spots left</span>
                    </div>
                    {c.program_name && <div className="mt-1.5 text-xs text-muted-foreground flex items-center gap-1.5"><GraduationCap size={12} /> {c.program_name}</div>}
                    <div className="mt-3 pt-3 border-t border-border/60 flex gap-2">
                      {avail.dogs.length > 1 && (
                        <Select value={dogId} onValueChange={(v) => setDogByClass({ ...dogByClass, [c.id]: v })}>
                          <SelectTrigger className="h-8 w-28" data-testid="available-dog-select"><SelectValue /></SelectTrigger>
                          <SelectContent>{avail.dogs.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                      <Button size="sm" className="flex-1 h-8" disabled={booked} onClick={() => request(c.id)} data-testid="request-booking-btn">
                        {booked ? "Already booked" : full ? "Join waitlist" : "Request booking"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {avail.classes.length === 0 && <p className="text-muted-foreground col-span-full text-center py-12">No classes available to book right now.</p>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
